import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';

import type { VideoSource } from '@/lib/videoSource';
import type { VoltrUploadPart } from '@/types/voltr';

/**
 * Video bytes for a Voltr upload, streamed from disk (lib/videoSource) a
 * range at a time: the checksum is built over 8 MiB reads and each part is
 * read just before its PUT, so memory stays near one part whatever the
 * file's size. Reading uses expo-file-system, which `expo` already links
 * into the shipped binary; package.json pins it to that exact version so
 * this ships over OTA. MAX_VIDEO_BYTES is Voltr's own limit.
 */
const MAX_VIDEO_MB = 500;
export const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;

/** How much of the file is read per checksum step (Voltr's part size). */
export const HASH_CHUNK_BYTES = 8 * 1024 * 1024;

export class VideoTooLargeError extends Error {
  constructor() {
    super(`That video is over ${MAX_VIDEO_MB} MB, too big to upload from the app. Trim it or export it at 1080p, then try again.`);
    this.name = 'VideoTooLargeError';
  }
}

/** Storage answered a part's PUT with a non-2xx (an expired URL, or a refusal): `status` says which. */
export class PartRefusedError extends Error {
  constructor(
    readonly part: number,
    readonly status: number,
  ) {
    super(`part ${part} failed (${status})`);
    this.name = 'PartRefusedError';
  }
}

/** Throws VideoTooLargeError past the cap. `sizeBytes` unknown (some web picks) passes; the opened file is checked again. */
export function assertUploadableSize(sizeBytes: number | null | undefined): void {
  if (sizeBytes != null && sizeBytes > MAX_VIDEO_BYTES) throw new VideoTooLargeError();
}

const yieldToUi = () => new Promise((resolve) => setTimeout(resolve, 0));

/** One range of the file, refusing a short read (the file changed under us, so the checksum would be wrong). */
async function readRange(source: VideoSource, offset: number, length: number): Promise<Uint8Array> {
  const bytes = await source.read(offset, length);
  if (bytes.length !== length) throw new Error(`read ${bytes.length} of ${length} bytes at ${offset}`);
  return bytes;
}

/**
 * Lowercase hex SHA-256 of the whole file, the checksum Voltr verifies the
 * finished upload against. Built a chunk at a time, yielding between chunks
 * so the screen keeps painting while a large file is hashed; `signal` stops it
 * between chunks.
 */
export async function sha256Of(source: VideoSource, signal?: AbortSignal, chunkBytes = HASH_CHUNK_BYTES): Promise<string> {
  const hash = sha256.create();
  for (let offset = 0; offset < source.size; offset += chunkBytes) {
    // Stopped (logout): don't keep reading a large file for nobody.
    if (signal?.aborted) throw new Error('upload stopped');
    hash.update(await readRange(source, offset, Math.min(chunkBytes, source.size - offset)));
    await yieldToUi();
  }
  return bytesToHex(hash.digest());
}

/**
 * How long one part may take before it's abandoned (a stalled connection
 * must not hang the upload). An 8 MiB part in 5 minutes is ~220 kbps, so a
 * slow uplink still gets through.
 */
export const PART_TIMEOUT_MS = 300_000;

/**
 * PUTs every part Voltr hasn't received yet straight to its pre-signed URL
 * (no bearer token; the URL is the credential), reading each part's range
 * just before its PUT. Parts go one at a time so a weak connection isn't
 * split across parallel requests and only one part is in memory. `signal`
 * stops it between parts and mid-part (logout); each part also times out on
 * its own. A non-2xx throws PartRefusedError.
 */
export async function putParts(
  source: VideoSource,
  parts: VoltrUploadPart[],
  onProgress: (sentBytes: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  let sent = parts.filter((p) => p.received).reduce((sum, p) => sum + p.bytes, 0);
  onProgress(sent);
  for (const part of parts) {
    if (part.received) continue;
    if (signal?.aborted) throw new Error('upload stopped');
    const slice = await readRange(source, part.offset, part.bytes);
    const response = await putPart(part, slice, signal);
    if (!response.ok) throw new PartRefusedError(part.number, response.status);
    sent += part.bytes;
    onProgress(sent);
  }
}

/** One part's PUT, aborted by the outer signal or its own timeout. */
async function putPart(part: VoltrUploadPart, slice: Uint8Array, signal?: AbortSignal): Promise<Response> {
  const controller = new AbortController();
  const stop = () => controller.abort();
  const timer = setTimeout(stop, PART_TIMEOUT_MS);
  signal?.addEventListener('abort', stop);
  // Stopped while the part was being read: the listener above won't fire for an abort that already happened.
  if (signal?.aborted) stop();
  try {
    return await fetch(part.url, { method: 'PUT', headers: part.headers, body: ownBuffer(slice), signal: controller.signal });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', stop);
  }
}

/** The bytes as a buffer of their own (a view into a larger buffer would send all of it). */
function ownBuffer(bytes: Uint8Array): ArrayBuffer {
  const whole = bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength;
  return (whole ? bytes : bytes.slice()).buffer as ArrayBuffer;
}
